import * as Yup from 'yup'

import type {
  Address,
  Customer,
  Maybe,
  Order,
  OrderAddress,
} from '@haus-storefront-react/shared-types'
import { createNameSchema, createPhoneSchema } from '../../helper/validation-schema'
import { isEmptyAddress, isSameAddress } from '../../helper/address-normalization'
import type { FieldConfig } from '../../ui/form-fields/FormFields'
import type { AddressData, CustomerDetailsData } from '../../types'
import { SelectCountry } from '../../ui/select-country/SelectCountry'
import { i18n } from '../../../../localization/i18n'

export type CustomerDetailsFormValues = CustomerDetailsData & {
  billingAddress: AddressData
}

export const CUSTOMER_DETAILS_FIELDS: FieldConfig[] = [
  {
    kind: 'row',
    fields: [
      { name: 'firstName', label: 'First name', width: 50, required: true },
      { name: 'lastName', label: 'Last name', width: 50, required: true },
    ],
  },
  { kind: 'field', name: 'phoneNumber', label: 'Phone number', required: true },
  { kind: 'field', name: 'emailAddress', label: 'Email address', required: true },
]

export const ADDRESS_FIELDS: FieldConfig[] = [
  {
    kind: 'row',
    fields: [
      { name: 'fullName', label: 'Name', required: true, width: 50 },
      { name: 'company', label: 'Company', required: false, width: 50 },
    ],
  },
  { kind: 'field', name: 'addressLine1', label: 'Address line 1', required: true },
  { kind: 'field', name: 'addressLine2', label: 'Address line 2', required: false },
  {
    kind: 'row',
    fields: [
      { name: 'postalCode', label: 'Postal code', required: true, width: 30 },
      { name: 'city', label: 'City', required: true, width: 70 },
    ],
  },
  { kind: 'field', name: 'province', label: 'Province', required: false },
  {
    kind: 'custom',
    name: 'countryCode',
    label: 'Country',
    required: true,
    Component: SelectCountry,
  },
  { kind: 'field', name: 'phoneNumber', label: 'Phone number', required: false },
]

const createAddressSchema = () =>
  Yup.object().shape({
    fullName: createNameSchema(
      'checkout.validation.nameRequired',
      'checkout.validation.nameInvalidCharacters',
      'Name',
    ),
    company: Yup.string(),
    addressLine1: Yup.string().required(i18n.t('checkout.validation.addressLine1Required')),
    addressLine2: Yup.string(),
    city: Yup.string().required(i18n.t('checkout.validation.cityRequired')),
    province: Yup.string(),
    postalCode: Yup.string().required(i18n.t('checkout.validation.postalCodeRequired')),
    countryCode: Yup.string().required(i18n.t('checkout.validation.countryRequired')),
    phoneNumber: createPhoneSchema(false),
  })

/**
 * Create the customer details validation schema with translated error messages.
 * When isLoggedIn is true, address fields are not validated (read-only from account).
 */
export const createCustomerDetailsValidationSchema = (isLoggedIn = false) => {
  const addressSchema = createAddressSchema()
  const baseSchema = {
    firstName: createNameSchema(
      'checkout.validation.firstNameRequired',
      'checkout.validation.nameInvalidCharacters',
      'First name',
    ),
    lastName: createNameSchema(
      'checkout.validation.lastNameRequired',
      'checkout.validation.nameInvalidCharacters',
      'Last name',
    ),
    phoneNumber: createPhoneSchema(true),
    emailAddress: Yup.string()
      .required(i18n.t('checkout.validation.emailRequired'))
      .email(i18n.t('checkout.validation.emailInvalid')),
    useSeparateBillingAddress: Yup.boolean(),
    shippingAddress: isLoggedIn
      ? addressSchema.optional()
      : addressSchema.required(i18n.t('checkout.validation.shippingAddressRequired')),
    billingAddress: Yup.object().when('useSeparateBillingAddress', {
      is: true,
      then: () =>
        isLoggedIn
          ? addressSchema.optional()
          : addressSchema.required(i18n.t('checkout.validation.billingAddressRequired')),
      otherwise: (schema) => schema.optional(),
    }),
  }
  return Yup.object().shape(baseSchema)
}

/**
 * Map Vendure Address (from Customer.addresses) to AddressData.
 * Vendure uses streetLine1, streetLine2, country { code }; we use addressLine1, addressLine2, countryCode.
 */
export function mapVendureAddressToAddressData(address: Maybe<Address> | undefined): AddressData {
  return {
    countryCode: address?.country?.code ?? '',
    fullName: address?.fullName ?? '',
    company: address?.company ?? '',
    addressLine1: address?.streetLine1 ?? '',
    addressLine2: address?.streetLine2 ?? '',
    city: address?.city ?? '',
    province: address?.province ?? '',
    postalCode: address?.postalCode ?? '',
    phoneNumber: address?.phoneNumber ?? '',
  }
}

const getAddressInitialValues = (address: Maybe<OrderAddress> | undefined): AddressData => ({
  fullName: address?.fullName ?? '',
  company: address?.company ?? '',
  addressLine1: address?.streetLine1 ?? '',
  addressLine2: address?.streetLine2 ?? '',
  city: address?.city ?? '',
  province: address?.province ?? '',
  postalCode: address?.postalCode ?? '',
  countryCode: address?.countryCode ?? '',
  phoneNumber: address?.phoneNumber ?? '',
})

const ADDRESS_COMPARE_KEYS: Array<keyof AddressData> = [
  'fullName',
  'company',
  'addressLine1',
  'addressLine2',
  'city',
  'province',
  'postalCode',
  'countryCode',
  'phoneNumber',
]

const isEmptyAddressForCustomerDetails = (address: AddressData): boolean =>
  isEmptyAddress(address, ADDRESS_COMPARE_KEYS)

const isSameAddressForCustomerDetails = (a: AddressData, b: AddressData): boolean =>
  isSameAddress(a, b, ADDRESS_COMPARE_KEYS)

export const customerDetailsInitialValues = (
  order: Maybe<Order> | undefined,
  activeCustomer?: Maybe<Customer>,
): CustomerDetailsFormValues => {
  const customer = order?.customer ?? activeCustomer

  let shippingAddress: AddressData
  let billingAddress: AddressData

  if (activeCustomer && activeCustomer.addresses?.length) {
    const addrs = activeCustomer.addresses
    const shippingAddr = addrs.find((a) => a.defaultShippingAddress === true) ?? addrs[0]
    const billingAddr = addrs.find((a) => a.defaultBillingAddress === true) ?? shippingAddr
    shippingAddress = mapVendureAddressToAddressData(shippingAddr)
    billingAddress = mapVendureAddressToAddressData(billingAddr)
  } else {
    shippingAddress = getAddressInitialValues(order?.shippingAddress)
    billingAddress = getAddressInitialValues(order?.billingAddress)
  }

  const hasSeparateBilling =
    !isEmptyAddressForCustomerDetails(billingAddress) &&
    !isSameAddressForCustomerDetails(billingAddress, shippingAddress)

  return {
    firstName: customer?.firstName ?? '',
    lastName: customer?.lastName ?? '',
    emailAddress: customer?.emailAddress ?? '',
    phoneNumber: customer?.phoneNumber ?? '',
    useSeparateBillingAddress: hasSeparateBilling,
    shippingAddress,
    billingAddress,
  }
}
